<template>
    <Modal v-if="visible" @cancel="onCancel">
        <ModalCardConfirm
            :activeValidate="canConfirm"
            :back="goPrev"
            :contentMaxHeight="contentMaxHeight"
            :first="step === 1 || step === initialStep"
            :headerTitle="title"
            :maxWidth="maxWidth"
            :overflowContent="step === 3 ? 'visible' : 'auto'"
            :width="width"
            @cancel="onCancel"
            @confirm="onConfirm"
        >
            <PreavisStepsBar :selectedEquipementsCount="selectedEquipementIds.length" :step="step" />
            <div class="fermeture-note">
                <ReglePastille cle="preavisIntervention.fermetureReinitialise" />
            </div>

            <div class="content-container">
                <StepOne
                    v-if="step === 1"
                    :title="$t('administratif.navigation.equipements.search')"
                    equipement-namespace="preavisEquipementModule"
                    site-getter="preavisInterventionState/getPreavisSite"
                    :site-mutation="PREAVIS_SITE_MUTATION"
                    :reset-to-step-mutation="''"
                    :set-equipement-mutation="''"
                    site-list-namespace="preavisSiteList"
                    style="max-height: 56vh"
                    @next="goNext"
                />

                <div v-else-if="step === 2">
                    <StepThree
                        :selectedIds="selectedEquipementIds"
                        :display-radio="false"
                        :multiple="true"
                        :open-info-equipement="false"
                        :excluded-equipement-ids="excludedEquipementIdsFromPreavis"
                        :allow-select-all="true"
                        equipement-namespace="preavisEquipementModule"
                        site-getter="preavisInterventionState/getPreavisSite"
                        :set-equipement-mutation="''"
                        style="max-height: 56vh"
                        @update:selectedIds="onSelectedIdsUpdate"
                    />

                    <ErrorMessageFormulaire
                        v-if="manuallyConflictingEquipement.length > 0"
                        :items="manuallyConflictingEquipement"
                        :message="$t('preavisIntervention.conflict.message')"
                    />
                    <ErrorMessageFormulaire
                        v-if="autoExcludedEquipement.length > 0"
                        :items="autoExcludedEquipement"
                        :message="$t('preavisIntervention.conflict.selectAll.message')"
                    />
                    <ReglePastille cle="preavisIntervention.conflitEquipement" />
                </div>

                <div v-else-if="step === 3" class="step-three-container">
                    <ReglePastille cle="preavisIntervention.objetParDefaut" />
                    <PreavisFormCreateEdit
                        ref="formulaireRef"
                        :initialObjet="initialObjet"
                        @state-change="onStepThreeStateChange"
                    />
                </div>
            </div>
        </ModalCardConfirm>
    </Modal>
    <WaitingModal v-if="isSubmitting" :text="$t('preavisIntervention.chargement.encours')" />
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Modal from '@/components/ui-components/Modal.vue';
import ModalCardConfirm from '@/components/ui-components/ModalCardConfirm.vue';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import StepOne from '@/modules/Intervention/components/StepOne.vue';
import StepThree from '@/modules/Intervention/components/StepThree.vue';
import PreavisFormCreateEdit from '@/modules/PreavisIntervention/components/PreavisFormCreateEdit.vue';
import PreavisStepsBar from '@/modules/PreavisIntervention/components/PreavisStepsBar.vue';
import PreavisInterventionService from '@/modules/PreavisIntervention/services/PreavisInterventionService';
import { Site } from '@/models/Site';
import { EquipementItems } from '@/models/EquipementItems';
import { PreavisInterventionDTO } from '@/models/PreavisIntervention/PreavisInterventionDTO';
import ErrorMessageFormulaire from '@/components/ui-components/ErrorMessageFormulaire.vue';
import { PreavisInterventionModuleAction } from '@/modules/PreavisIntervention/store/actions-types';
import { PREAVIS } from '@/modules/PreavisIntervention/store/mutation-types';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type StepThreeState = {
    isFormValid: boolean;
    objet: string;
    delai: number;
    actif: boolean;
    emails: string[];
};

export default defineComponent({
    name: 'PreavisCreateModal',
    components: {
        ErrorMessageFormulaire,
        PreavisFormCreateEdit,
        Modal,
        ModalCardConfirm,
        WaitingModal,
        StepOne,
        StepThree,
        PreavisStepsBar,
        ReglePastille,
    },
    props: {
        visible: { type: Boolean, required: true },
        title: { type: String },
        width: { type: String, default: '75vw' },
        maxWidth: { type: String, default: '80%' },
        contentMaxHeight: { type: String, default: '75vh' },
        initialStep: { type: Number, default: 1 },
        preselectedEquipement: { type: Object as PropType<EquipementItems>, default: null },
        preselectedSiteId: { type: Number, default: null },
    },
    emits: ['cancel', 'created'],
    data() {
        return {
            PreavisInterventionModuleAction,
            PREAVIS_SITE_MUTATION: `preavisInterventionState/${PREAVIS.SET_PREAVIS_SITE}`,
            step: this.initialStep as number,
            selectedEquipementIds: this.preselectedEquipement ? [this.preselectedEquipement.id] : [],
            initialObjet: '' as string,
            stepThreeState: null as StepThreeState | null,
            equipements: [] as EquipementItems[],
            preavisBySite: [] as PreavisInterventionDTO[],
            selectAllPerformed: false,
        };
    },

    computed: {
        isSubmitting(): boolean {
            return this.$store.state.preavisInterventionState.isSubmitting;
        },
        selectedSite(): Site | undefined {
            if (this.preselectedEquipement) {
                const siteId = this.preselectedSiteId ?? this.preselectedEquipement.adresseProjetId;
                return siteId != null ? ({ id: siteId } as Site) : undefined;
            }
            return this.$store.getters['preavisInterventionState/getPreavisSite'];
        },
        canConfirm(): boolean {
            if (this.step === 1) return !!this.selectedSite?.id;
            if (this.step === 2)
                return this.selectedEquipementIds.length > 0 && this.manuallyConflictingEquipement.length === 0;
            return !!this.stepThreeState?.isFormValid && !this.isSubmitting;
        },
        existingPreavis(): PreavisInterventionDTO[] {
            return this.preavisBySite;
        },
        manuallyConflictingEquipement(): string[] {
            if (this.selectAllPerformed || !this.selectedEquipementIds.length || !this.existingPreavis.length)
                return [];
            const equipementList: EquipementItems[] = this.getEquipementList();
            const matched = this.selectedEquipementIds.map(phaseId =>
                equipementList.find((e: EquipementItems) => e.phaseId === phaseId)
            );
            return matched
                .filter((eq): eq is EquipementItems => !!eq)
                .filter(eq => {
                    return this.existingPreavis.some(p => String(p.equipement?.id) === String(eq.id));
                })
                .map(eq => eq.libelle);
        },
        autoExcludedEquipement(): string[] {
            if (!this.existingPreavis.length) return [];
            const equipementList: EquipementItems[] = this.getEquipementList();
            const allExcluded = equipementList.length > 0 && equipementList.every(eq =>
                this.excludedEquipementIdsFromPreavis.includes(String(eq.id))
            );
            if (!this.selectAllPerformed && !allExcluded) return [];
            return equipementList
                .filter(eq => {
                    return this.existingPreavis.some(p => String(p.equipement?.id) === String(eq.id));
                })
                .map(eq => eq.libelle);
        },
        excludedEquipementIdsFromPreavis(): string[] {
            return this.existingPreavis
                .map(p => String(p.equipement?.id))
                .filter(id => id && id !== 'undefined');
        },
    },
    watch: {
        step: {
            handler(newStep: number) {
                if (newStep === 2 && this.selectedSite?.id) {
                    PreavisInterventionService.getPreavisInterventions(this.selectedSite.id as number)
                        .then(res => {
                            this.preavisBySite = res.data;
                        })
                        .catch(() => {
                            this.preavisBySite = [];
                        });
                }
                if (newStep === 3 && !this.initialObjet) {
                    this.initialObjet = this.initialiserObjet();
                }
            },
            immediate: true,
        },
        visible(newVal: boolean) {
            if (!newVal) {
                this.reset();
            }
        },
    },
    methods: {
        getEquipementList() {
            return this.$store.getters['preavisEquipementModule/getEquipementList'];
        },
        goNext() {
            if (this.step < 3) this.step += 1;
        },
        goPrev() {
            if (this.step > this.initialStep) {
                if (this.step === 3) this.initialObjet = '';
                if (this.step === 2) {
                    this.selectedEquipementIds = [];
                    this.selectAllPerformed = false;
                }
                this.step -= 1;
            }
        },
        onStepThreeStateChange(payload: StepThreeState) {
            this.stepThreeState = payload;
        },
        onSelectedIdsUpdate(ids: number[]) {
            this.selectedEquipementIds = ids;
            const equipementList: EquipementItems[] = this.getEquipementList();
            const selectableIds = equipementList
                .filter(eq => !this.excludedEquipementIdsFromPreavis.includes(String(eq.id)))
                .map(eq => eq.phaseId);
            this.selectAllPerformed =
                selectableIds.length > 0 && selectableIds.every(id => ids.includes(id));
        },
        onCancel() {
            this.$emit('cancel');
        },
        async onConfirm() {
            if (this.step < 3) {
                this.goNext();
                return;
            }
            (this.$refs.formulaireRef as any)?.validate();
            if (!this.stepThreeState?.isFormValid || this.isSubmitting) return;

            const equipementsIds = this.preselectedEquipement
                ? [this.preselectedEquipement.id]
                : this.selectedEquipementIds
                      .map(id => this.getEquipementList().find((e: EquipementItems) => e.phaseId === id)?.id)
                      .filter((equipementId): equipementId is number => equipementId !== undefined);

            try {
                await this.$store.dispatch(`preavisInterventionState/${PreavisInterventionModuleAction.CREATE}`, {
                    siteId: this.preselectedEquipement
                        ? this.preselectedEquipement.adresseProjetId
                        : this.selectedSite?.id,
                    equipementsIds: equipementsIds,
                    objet: this.stepThreeState.objet.trim(),
                    delai: this.stepThreeState.delai,
                    actif: this.stepThreeState.actif,
                    emails: this.stepThreeState.emails,
                });

                const error = this.$store.getters['preavisInterventionState/error'];
                if (!error) {
                    this.reset();
                    this.$emit('created');
                }
            } catch (e) {
                console.error('Erreur lors de la création :', e);
            }
        },
        reset() {
            this.step = this.initialStep;
            this.selectedEquipementIds = [];
            this.initialObjet = '';
            this.stepThreeState = null;
            this.preavisBySite = [];
            this.selectAllPerformed = false;
            // Reset du state preavis isolé
            this.$store.commit(`preavisInterventionState/${PREAVIS.SET_PREAVIS_SITE}`, undefined);
            // Reset du preavisEquipementModule
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_ITEMS', []);
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_PAGE', 0);
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_NUMBER_OF_PAGES', 0);
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_FILTER', '');
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_GROUPE_FILTER', '');
            this.$store.commit('preavisEquipementModule/SET_EQUIPEMENT_SS_GROUPE_FILTER', '');
            // Reset du preavisSiteList
            this.$store.commit('preavisSiteList/SITE_LIST_RESET');
        },
        initialiserObjet() {
            if (this.preselectedEquipement) {
                return `${this.$t('preavisIntervention.objet.alerte')} ${this.preselectedEquipement.libelle}`;
            }

            if (this.selectedEquipementIds.length === 1) {
                const eq = this.getEquipementList().find(
                    (e: EquipementItems) => e.phaseId === this.selectedEquipementIds[0]
                );

                return eq
                    ? `${this.$t('preavisIntervention.objet.alerte')} ${eq.libelle}`
                    : this.$tc('preavisIntervention.objet.default', 1);
            }

            return this.$tc('preavisIntervention.objet.default', this.selectedEquipementIds.length);
        },
    },
});
</script>

<style scoped>
.content-container {
    margin-top: 8px;
}
.fermeture-note {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
}
.step-three-container {
    position: relative;
}
.step-three-container .regle-pastille {
    position: absolute;
    top: -8px;
    right: 0;
}
.clearBtn{
     display: flex;
     justify-content: center;
     align-items: center;
     cursor: pointer;
     border: 1px solid var(--color-principal);
     border-radius: 4px;
     padding: 10px;
     text-transform: uppercase;
     font-weight: 600;
     max-height: 50px;
 }
</style>
